import React, { useState } from "react";
// Using simple HTML icons instead of react-icons for TypeScript compatibility
import ReportModal from "./ReportModal";

interface ApiResponse {
  metrics: {
    revenueGain: number;
    hoursSaved: number;
    patientRetention: number;
    documentationAccuracy: number;
    costSavings?: number; // Add optional costSavings to metrics
  };
  perTherapist: {
    revenueGain: number;
    hoursSaved: number;
    costSavings: number;
  };
}

interface DetailedResultsProps {
  results: ApiResponse;
  formData?: any; // Quick calculator data
  detailedFormData?: any; // Detailed calculator data
}

const DetailedResults = ({ results, formData, detailedFormData }: DetailedResultsProps) => {
  const [showReportModal, setShowReportModal] = useState(false);

  return (
    <>
      <div className="w-full bg-white/80 backdrop-blur-sm rounded-lg p-8 shadow-lg">
        {/* Overall Clinic Estimates */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
            <svg className="mr-3 w-6 h-6 text-[#619ed3]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 2h8v8H6V6z" clipRule="evenodd" />
            </svg>
            Overall Clinic Estimates
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-[#619ed3]">
            ${results.metrics.revenueGain.toLocaleString()}
          </div>
              <div className="text-sm text-slate-500">Revenue Gain</div>
        </div>
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-[#619ed3]">
                {results.metrics.hoursSaved.toFixed(0)} hrs
          </div>
              <div className="text-sm text-slate-500">Hours Saved Annually</div>
        </div>
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-[#619ed3]">
            {results.metrics.patientRetention}%
              </div>
              <div className="text-sm text-slate-500">Patient Retention</div>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-[#619ed3]">
                {results.metrics.documentationAccuracy}%
              </div>
              <div className="text-sm text-slate-500">Documentation Accuracy</div>
            </div>
          </div>
        </div>

        {/* Per Clinician Estimates */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
            <svg className="mr-3 w-6 h-6 text-[#619ed3]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
            </svg>
            Per Clinician Estimates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-[#619ed3]">
                $
                {results.perTherapist.revenueGain.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </div>
              <div className="text-sm text-slate-500">Revenue Gain</div>
          </div>
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-[#619ed3]">
                {results.perTherapist.hoursSaved.toFixed(0)} hrs
              </div>
              <div className="text-sm text-slate-500">Time Saved</div>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-[#619ed3]">
                $
                {results.perTherapist.costSavings.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </div>
              <div className="text-sm text-slate-500">Cost Savings</div>
            </div>
          </div>
        </div>

        {/* Divider Line */}
        <div className="border-t border-gray-300 my-8"></div>
        
        {/* Save Results Button - Centered */}
        <div className="flex justify-center">
          <button
            onClick={() => setShowReportModal(true)}
            className="inline-block border-2 border-[#1e3d3b] text-[#1e3d3b] font-semibold px-8 py-3 rounded-full hover:bg-[#1e3d3b] hover:text-white transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Save Your Results
          </button>
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          results={results}
          formData={formData}
          detailedFormData={detailedFormData}
        />
      )}
    </>
  );
};

export default DetailedResults;
